'use client';

import React from 'react';
import { cn } from '../utils/cn';

export type SheetSide = 'left' | 'right' | 'top' | 'bottom' | 'center';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  side?: SheetSide;
  /** Container className applied to the sheet panel. */
  className?: string;
  /** Optional override for the scrim/backdrop className. */
  scrimClassName?: string;
  children: React.ReactNode;
  /** ARIA label for the dialog. */
  'aria-label'?: string;
  /** ID linking the sheet to a header element via aria-labelledby. */
  'aria-labelledby'?: string;
  /** Width preset (only relevant for left/right sides). */
  width?: 'sm' | 'md' | 'lg' | 'xl';
  /** Disable Escape key close behavior. */
  disableEscape?: boolean;
}

const widthMap = {
  sm: 'w-[min(20rem,92vw)]',
  md: 'w-[min(24rem,92vw)]',
  lg: 'w-[min(32rem,94vw)]',
  xl: 'w-[min(48rem,96vw)]',
};

export const Sheet: React.FC<SheetProps> = ({
  open,
  onClose,
  side = 'right',
  className,
  scrimClassName,
  width = 'md',
  children,
  disableEscape,
  ...ariaProps
}) => {
  React.useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || disableEscape) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, disableEscape]);

  if (!open) return null;

  const positionStyles =
    side === 'right'
      ? `top-0 bottom-0 right-0 ${widthMap[width]} animate-slide-in-right`
      : side === 'left'
        ? `top-0 bottom-0 left-0 ${widthMap[width]} animate-slide-in-left`
        : side === 'top'
          ? 'top-0 inset-x-0 max-h-[min(85dvh,85vh)] animate-slide-in-bottom'
          : side === 'bottom'
            ? 'bottom-0 inset-x-0 max-h-[min(85dvh,85vh)] rounded-t-xl animate-slide-in-bottom'
            : 'inset-0 m-auto max-h-[min(90dvh,90vh)] w-[min(32rem,92vw)] rounded-md animate-scale-in';

  return (
    <div className="fixed inset-0 z-50">
      <div
        className={cn(
          'fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in',
          scrimClassName,
        )}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'fixed z-10 flex flex-col overflow-hidden bg-surface text-surface-foreground shadow-level-3',
          positionStyles,
          className,
        )}
        {...ariaProps}
      >
        {children}
      </div>
    </div>
  );
};

export const SheetHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('flex items-start justify-between gap-3 border-b border-outline/40 px-5 py-4', className)} {...props} />
);

export const SheetTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ className, ...props }) => (
  <h2 className={cn('text-title-lg font-semibold text-surface-foreground', className)} {...props} />
);

export const SheetBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 py-4', className)} {...props} />
);

export const SheetFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('flex items-center justify-end gap-2 border-t border-outline/40 px-5 py-3', className)} {...props} />
);
